<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
  avatarSrc: {
    type: String,
    default: '',
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'goToContactsList',
  'toggleBlock',
  'uploadAvatar',
  'deleteAvatar',
]);

const { t } = useI18n();
const route = useRoute();

const contactId = computed(() => route.params.contactId);

const selectedContactName = computed(() => {
  return props.selectedContact?.name;
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS'),
      link: '#',
    },
  ];
  if (props.selectedContact) {
    items.push({
      label: selectedContactName.value,
    });
  }
  return items;
});

const isContactBlocked = computed(() => {
  return props.selectedContact?.blocked;
});

const handleBreadcrumbClick = () => {
  emit('goToContactsList');
};

const toggleBlock = () => {
  emit('toggleBlock', isContactBlocked.value);
};
</script>

<template>
  <section
    class="flex w-full h-full overflow-hidden justify-evenly bg-n-surface-1"
  >
    <div class="flex flex-col w-full h-full transition-all duration-300">
      <header
        class="sticky top-0 z-10 px-6 border-b bg-n-surface-1 border-n-weak"
      >
        <Breadcrumb
          :items="breadcrumbItems"
          class="pt-6"
          @click="handleBreadcrumbClick"
        />
        <div
          class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full gap-3 pt-4 pb-6"
        >
          <div class="flex items-center min-w-0 gap-3">
            <Avatar
              :src="avatarSrc || ''"
              :name="selectedContactName || ''"
              :size="56"
              :status="selectedContact?.availabilityStatus"
              hide-offline-status
              allow-upload
              @upload="emit('uploadAvatar', $event)"
              @delete="emit('deleteAvatar')"
            />
            <div class="flex flex-col min-w-0 gap-0.5">
              <span class="text-base font-medium truncate text-n-slate-12">
                {{ selectedContactName }}
              </span>
              <div
                class="flex items-center gap-2 text-sm truncate text-n-slate-11"
              >
                <span v-if="selectedContact?.email" class="truncate">
                  {{ selectedContact.email }}
                </span>
                <span
                  v-if="selectedContact?.email && selectedContact?.phoneNumber"
                  class="text-n-slate-8"
                >
                  •
                </span>
                <span v-if="selectedContact?.phoneNumber" class="truncate">
                  {{ selectedContact.phoneNumber }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <Button
              :label="
                !isContactBlocked
                  ? $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
                  : $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
              "
              size="sm"
              slate
              :is-loading="isUpdating"
              :disabled="isUpdating"
              @click="toggleBlock"
            />
            <VoiceCallButton
              :phone="selectedContact?.phoneNumber"
              :contact-id="contactId"
              :label="$t('CONTACT_PANEL.CALL')"
              size="sm"
            />
            <ComposeConversation :contact-id="contactId">
              <template #trigger="{ toggle }">
                <Button
                  :label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
                  size="sm"
                  @click="toggle"
                />
              </template>
            </ComposeConversation>
          </div>
        </div>
      </header>
      <main class="flex flex-1 overflow-hidden">
        <slot name="default" />
      </main>
    </div>
  </section>
</template>
